import { type Day, type CutoffTime } from '@choco-admin/types';
import { Spinner } from '@heroui/react';
import React, { useCallback, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BaseModal } from '../../../../../../../components/BaseModal';
import { DeliveryDaySettings } from '../../../../../../../components/DeliveryDaySettings';
import { ChocoCheckbox } from '../../../../../../../components/FormComponents';
import { useDeleteDeliverySetting } from '../../../../../hooks/useDeleteDeliverySetting';
import { useSupplierDeliverySetting } from '../../../../../hooks/useSupplierDeliverySetting';
import { useUpdateDeliverySetting } from '../../../../../hooks/useUpdateDeliverySetting';

import {
  CutoffTypeSelect,
  type CutoffType,
} from './components/CutoffTypeSelect';

interface DeliverySettingsFormData {
  hasCustomDeliverySettings: boolean;
  cutoffType: CutoffType;
  deliveryDays: Record<Day, boolean>;
  cutoffTimes: Record<Day, CutoffTime>;
  cutoffDays: Record<Day, string>;
}

interface DeliverySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
}

const DAYS: Day[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// Company defaults shown when the chat has no custom delivery settings:
// every day available with no cutoff.
const DEFAULT_DELIVERY_DAYS: Record<Day, boolean> = {
  Monday: true,
  Tuesday: true,
  Wednesday: true,
  Thursday: true,
  Friday: true,
  Saturday: true,
  Sunday: true,
};

const DEFAULT_CUTOFF_TIMES: Record<Day, CutoffTime> = {
  Monday: '23:59:59',
  Tuesday: '23:59:59',
  Wednesday: '23:59:59',
  Thursday: '23:59:59',
  Friday: '23:59:59',
  Saturday: '23:59:59',
  Sunday: '23:59:59',
};

const DEFAULT_CUTOFF_DAYS: Record<Day, string> = {
  Monday: '1',
  Tuesday: '1',
  Wednesday: '1',
  Thursday: '1',
  Friday: '1',
  Saturday: '1',
  Sunday: '1',
};

const DEFAULT_VALUES: DeliverySettingsFormData = {
  hasCustomDeliverySettings: false,
  cutoffType: 'no_cutoff',
  deliveryDays: DEFAULT_DELIVERY_DAYS,
  cutoffTimes: DEFAULT_CUTOFF_TIMES,
  cutoffDays: DEFAULT_CUTOFF_DAYS,
};

/**
 * Chat delivery days & cutoff times, isolated in a modal with its own save
 * button and its own mutation (updateSupplierDeliverySetting, chat scope). The
 * chat settings form does not own these fields — saving here is independent of
 * the surrounding Settings form.
 */
export const DeliverySettingsModal: React.FC<DeliverySettingsModalProps> = ({
  isOpen,
  onClose,
  chatId,
}) => {
  const { mappedSettings, isLoading, refetch } = useSupplierDeliverySetting({
    id: chatId,
  });

  const formMethods = useForm<DeliverySettingsFormData>({
    defaultValues: DEFAULT_VALUES,
  });
  const {
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = formMethods;

  // Keep the modal open after a successful save; refresh the summary behind it
  // and clear the dirty state so Save disables until the next edit.
  const onSaved = useCallback(() => {
    refetch();
    reset(getValues());
  }, [refetch, reset, getValues]);

  const { updateSettings, isUpdating } = useUpdateDeliverySetting({
    onSuccess: onSaved,
  });
  const { deleteSettings, isDeleting } = useDeleteDeliverySetting({
    onSuccess: onSaved,
  });

  // Seed the form from the loaded settings once per open, then let the user
  // edit freely. mapDeliveryDays() returns a fresh object each render, so a
  // ref guards against re-seeding (which would clobber edits) on every render.
  const seeded = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      seeded.current = false;
      return;
    }
    if (seeded.current || !mappedSettings) return;

    reset({
      hasCustomDeliverySettings: true,
      cutoffType: mappedSettings.cutoffType as CutoffType,
      deliveryDays: mappedSettings.deliveryDays,
      cutoffTimes: mappedSettings.cutoffTimes,
      cutoffDays: mappedSettings.cutoffDays,
    });
    seeded.current = true;
  }, [isOpen, mappedSettings, reset]);

  const hasCustomDeliverySettings = watch('hasCustomDeliverySettings') ?? false;
  const cutoffType = watch('cutoffType') ?? 'no_cutoff';
  const deliveryDays = watch('deliveryDays') ?? DEFAULT_DELIVERY_DAYS;
  const cutoffTimes = watch('cutoffTimes') ?? DEFAULT_CUTOFF_TIMES;
  const cutoffDays = watch('cutoffDays') ?? DEFAULT_CUTOFF_DAYS;

  const handleToggleCustomSettings = useCallback(
    (isSelected: boolean) => {
      setValue('hasCustomDeliverySettings', isSelected, { shouldDirty: true });
    },
    [setValue]
  );

  const handleDayToggle = useCallback(
    (day: Day) => {
      setValue(`deliveryDays.${day}`, !deliveryDays[day], {
        shouldDirty: true,
      });
    },
    [setValue, deliveryDays]
  );

  const handleCutoffTimeChange = useCallback(
    (day: Day, time: CutoffTime) => {
      setValue(`cutoffTimes.${day}`, time, { shouldDirty: true });
    },
    [setValue]
  );

  const handleCutoffDayChange = useCallback(
    (day: Day, cutoffDay: string) => {
      setValue(`cutoffDays.${day}`, cutoffDay, { shouldDirty: true });
    },
    [setValue]
  );

  const handleSave = useCallback(() => {
    const values = getValues();
    // Toggling custom settings off removes them so the chat reverts to the
    // company defaults; on saves/creates the chat-specific setting.
    if (!values.hasCustomDeliverySettings) {
      deleteSettings(chatId);
      return;
    }
    updateSettings({
      chatId,
      deliveryDays: values.deliveryDays,
      cutoffType: values.cutoffType,
      cutoffTimes: values.cutoffTimes,
      cutoffDays: values.cutoffDays,
    });
  }, [getValues, updateSettings, deleteSettings, chatId]);

  return (
    <BaseModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="2xl"
      title="Delivery Days & Cutoff Times"
      subtitle="Set customer specific values that differ from the company settings. These settings apply for all products."
      footerContent={{
        cancelText: 'Cancel',
        confirmText: 'Save',
        onCancel: onClose,
        onConfirm: handleSave,
        isLoading: isUpdating || isDeleting,
        isDisabled: !isDirty,
      }}
    >
      {isLoading && !mappedSettings ? (
        <div className="flex justify-center py-10">
          <Spinner label="Loading delivery settings…" />
        </div>
      ) : (
        <FormProvider {...formMethods}>
          <div className="space-y-6">
            <div className="border-b border-default-200 pb-6">
              <ChocoCheckbox
                isSelected={hasCustomDeliverySettings}
                onValueChange={handleToggleCustomSettings}
                size="sm"
                label="Enable customer specific delivery days & cutoff settings"
              />
            </div>

            {hasCustomDeliverySettings && (
              <div className="space-y-6">
                <CutoffTypeSelect />

                <div className="space-y-4">
                  {DAYS.map((day) => (
                    <DeliveryDaySettings
                      key={day}
                      day={day}
                      isEnabled={deliveryDays[day] || false}
                      cutoffTime={cutoffTimes[day] || '23:59:59'}
                      cutoffDay={cutoffDays[day] || '1'}
                      onToggle={handleDayToggle}
                      onCutoffTimeChange={handleCutoffTimeChange}
                      onCutoffDayChange={handleCutoffDayChange}
                      cutoffType={cutoffType}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </FormProvider>
      )}
    </BaseModal>
  );
};
